import { useEffect, useState } from "react"
import { collection, getDocs, limit, orderBy, query, where, Timestamp } from "firebase/firestore"
import { orderBy as orderByLodash } from "lodash"
import { db } from "@/config/firebase"
import { useAuth } from "@/features/auth/context/AuthContext"

export interface TransactionEntry {
  id: string
  packId: string
  creditsGranted: number
  amount: number
  currency: string
  status: string
  gateway: string
  createdAt: Date | null
  paidAt: Date | null
}

const TRANSACTION_LIMIT = 10

const toDate = (value: unknown): Date | null => {
  if (value instanceof Timestamp) return value.toDate()
  if (value instanceof Date) return value
  return null
}

// const stripeCredits = (items: unknown[]): number =>
//   (items ?? []).reduce((total: number, item: unknown) => {
//     const raw = (item as { price?: { transform_quantity?: { divide_by?: string | number } } })?.price
//       ?.transform_quantity?.divide_by
//     const n = Number.parseInt(String(raw ?? "0"), 10)
//     return total + (Number.isFinite(n) && n > 0 ? n : 0)
//   }, 0)

const useTransactionHistory = () => {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState<TransactionEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user?.uid) {
      setTransactions([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    const fetchTransactions = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Query Razorpay orders
        const razorpayQuery = query(
          collection(db, "razorpayOrders"),
          where("uid", "==", user.uid),
          orderBy("createdAt", "desc"),
          limit(TRANSACTION_LIMIT)
        )

        // Query Stripe payments (succeeded only for history display)
        // const stripeQuery = query(
        //   collection(db, "customers", user.uid, "payments"),
        //   where("status", "==", "succeeded"),
        //   orderBy("created", "desc"),
        //   limit(TRANSACTION_LIMIT)
        // )

        const [razorpaySnap] = await Promise.all([getDocs(razorpayQuery)])

        const razorpayEntries: TransactionEntry[] = razorpaySnap.docs.map(doc => {
          const data = doc.data()
          return {
            id: doc.id,
            packId: data.packId ?? "",
            creditsGranted: data.creditsGranted ?? 0,
            amount: data.amount ?? 0,
            currency: data.currency ?? "INR",
            status: data.status ?? "pending",
            gateway: "razorpay",
            createdAt: toDate(data.createdAt),
            paidAt: toDate(data.paidAt)
          }
        })

        // const stripeEntries: TransactionEntry[] = stripeSnap.docs.map(doc => {
        //   const data = doc.data()
        //   const created = toDate(data.created)
        //   return {
        //     id: doc.id,
        //     packId: "",
        //     creditsGranted: stripeCredits(Array.isArray(data.items) ? (data.items as unknown[]) : []),
        //     amount: data.amount_total ?? data.amount ?? 0,
        //     currency: data.currency ?? "usd",
        //     status: "paid",
        //     gateway: "stripe",
        //     createdAt: created,
        //     paidAt: created
        //   }
        // })

        const all = [...razorpayEntries]
        if (!cancelled) setTransactions(orderByLodash(all, [t => t.paidAt ?? t.createdAt ?? 0], ["desc"]))
      } catch (err) {
        if (!cancelled) setError("Failed to load transaction history.")
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchTransactions()
    return () => {
      cancelled = true
    }
  }, [user?.uid])

  return { transactions, isLoading, error }
}

export { useTransactionHistory }
